'use client';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import Badge from '@/components/ui/Badge';
import { createClient } from '@/lib/supabase/client';
import {
  Search, X, Loader2, BookOpen, Mic, FileText, ScrollText,
  ThumbsUp, MessageSquare, CheckCircle2, Compass, PenLine,
  ChevronDown, ChevronUp, Users, TrendingUp, Sparkles, Filter
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Constants ────────────────────────────────────────────────────────────────

const ERA_LABELS: Record<string, string> = {
  'era-1': 'Early Iron Age',
  'era-2': 'Kingdom of Mutapa',
  'era-3': 'Rozvi Empire',
  'era-4': 'Colonial Contact',
  'era-5': 'Anti Colonial Resistance',
  'era-6': 'Nationalist Period',
  'era-7': 'Post-Independence',
};

const ERA_KEYS = Object.keys(ERA_LABELS);

const CERTAINTY_META: Record<string, { label: string; icon: React.ElementType; badge: 'oral' | 'sourced' | 'firsthand'; color: string }> = {
  oral_tradition:    { label: 'Oral Tradition',    icon: Mic,      badge: 'oral',      color: '#C17A3E' },
  sourced_claim:     { label: 'Sourced Claim',     icon: BookOpen, badge: 'sourced',   color: '#008080' },
  firsthand_account: { label: 'Firsthand Account', icon: FileText, badge: 'firsthand', color: '#E05A47' },
};

const SORT_OPTIONS = [
  { value: 'newest',        label: 'Newest First' },
  { value: 'oldest',        label: 'Oldest First' },
  { value: 'most_upvoted',  label: 'Most Upvoted' },
  { value: 'most_discussed',label: 'Most Discussed' },
  { value: 'corroborated',  label: 'Most Corroborated' },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface DiscoverContribution {
  id: string;
  title: string;
  excerpt: string | null;
  certainty_label: string;
  era_id: string | null;
  topic_id: string | null;
  topic_label: string | null;
  region: string | null;
  word_count: number;
  created_at: string;
  author_name: string | null;
  author_id: string | null;
  is_anonymous: boolean;
  upvote_count: number;
  comment_count: number;
  corroboration_count: number;
}

interface TopicOption { id: string; label: string; count: number; }
interface ContributorOption { id: string; name: string; count: number; }

// ─── Gap Badge ────────────────────────────────────────────────────────────────

function GapBadge({ era, topic, count }: { era: string; topic: string; count: number }) {
  return (
    <Link
      href={`/contribution-submission?era=${era}&topic=${topic}`}
      className="group flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-150 hover:-translate-y-0.5"
      style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--primary)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 2px 12px rgba(224,90,71,0.12)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
      }}
    >
      <Sparkles size={12} style={{ color: 'var(--primary)' }} />
      <span className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>{ERA_LABELS[era] || era}</span>
      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>·</span>
      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{topic}</span>
      <span className="text-xs px-1.5 py-0.5 rounded-full ml-auto" style={{ backgroundColor: 'rgba(224,90,71,0.10)', color: 'var(--primary)' }}>
        {count} {count === 1 ? 'story' : 'stories'}
      </span>
      <span className="text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--primary)' }}>
        Fill gap →
      </span>
    </Link>
  );
}

// ─── Contribution Card ────────────────────────────────────────────────────────

function ContributionCard({ item }: { item: DiscoverContribution }) {
  const meta = CERTAINTY_META[item.certainty_label] || CERTAINTY_META.oral_tradition;
  const date = new Date(item.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <Link
      href={`/contributions/${item.id}`}
      className="group flex flex-col rounded-xl border overflow-hidden transition-all duration-200 hover:-translate-y-0.5"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--primary)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 4px 24px rgba(224,90,71,0.10)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
      }}
    >
      <div className="h-0.5 w-full" style={{ backgroundColor: meta.color, opacity: 0.7 }} />
      <div className="flex flex-col flex-1 p-5 gap-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <Badge variant={meta.badge} />
          <div className="flex items-center gap-1.5 flex-wrap">
            {item.era_id && ERA_LABELS[item.era_id] && (
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                {ERA_LABELS[item.era_id]}
              </span>
            )}
            {item.topic_label && (
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(0,128,128,0.08)', color: '#008080' }}>
                {item.topic_label}
              </span>
            )}
          </div>
        </div>
        <h3 className="text-sm font-semibold leading-snug line-clamp-2" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}>
          {item.title}
        </h3>
        {item.excerpt && (
          <p className="text-xs line-clamp-3 flex-1" style={{ color: 'var(--muted-foreground)' }}>
            {item.excerpt}
          </p>
        )}
        <div className="flex items-center justify-between pt-2 border-t text-xs" style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}>
          <span className="truncate max-w-[120px]">
            {item.is_anonymous ? 'Anonymous' : (item.author_name || 'Unknown')}
          </span>
          <div className="flex items-center gap-2.5">
            {item.corroboration_count > 0 && (
              <span className="flex items-center gap-0.5" style={{ color: '#22c55e' }}>
                <CheckCircle2 size={10} />
                {item.corroboration_count}
              </span>
            )}
            {item.upvote_count > 0 && (
              <span className="flex items-center gap-0.5">
                <ThumbsUp size={10} />
                {item.upvote_count}
              </span>
            )}
            {item.comment_count > 0 && (
              <span className="flex items-center gap-0.5">
                <MessageSquare size={10} />
                {item.comment_count}
              </span>
            )}
            <span>{date}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// ─── Stat Pill ────────────────────────────────────────────────────────────────

function StatPill({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: number | string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
      <Icon size={15} style={{ color: 'var(--primary)' }} />
      <div>
        <p className="text-base font-bold leading-none" style={{ color: 'var(--foreground)' }}>{value}</p>
        <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{label}</p>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DiscoverPage() {
  const [contributions, setContributions] = useState<DiscoverContribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [topics, setTopics] = useState<TopicOption[]>([]);
  const [contributors, setContributors] = useState<ContributorOption[]>([]);
  const [gapEras, setGapEras] = useState<{ era: string; topic: string; count: number }[]>([]);

  // Filters
  const [keyword, setKeyword] = useState('');
  const [eraFilter, setEraFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [contributorFilter, setContributorFilter] = useState('');
  const [certaintyFilter, setCertaintyFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 18;

  useEffect(() => {
    const supabase = createClient();

    async function load() {
      setLoading(true);

      // Fetch published contributions
      const { data: contribData } = await supabase
        .from('contributions')
        .select('id, title, excerpt, certainty_label, era_id, topic_id, region, word_count, created_at, is_anonymous, author_id')
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      const rows = contribData || [];

      // Author names
      const authorIds = [...new Set(rows.filter((r: any) => !r.is_anonymous && r.author_id).map((r: any) => r.author_id))];
      let nameMap: Record<string, string> = {};
      if (authorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, full_name, username')
          .in('id', authorIds);
        for (const p of profiles || []) {
          nameMap[p.id] = p.full_name || p.username || 'Unknown';
        }
      }

      // Topic labels
      const topicIds = [...new Set(rows.filter((r: any) => r.topic_id).map((r: any) => r.topic_id))];
      let topicMap: Record<string, string> = {};
      if (topicIds.length > 0) {
        const { data: topicRows } = await supabase
          .from('topics')
          .select('id, label')
          .in('id', topicIds);
        for (const t of topicRows || []) {
          topicMap[t.id] = t.label;
        }
      }

      // Upvotes
      const contribIds = rows.map((r: any) => r.id);
      let upvoteMap: Record<string, number> = {};
      let commentMap: Record<string, number> = {};
      let corrobMap: Record<string, number> = {};
      if (contribIds.length > 0) {
        const [upvoteResult, commentResult, corrobResult] = await Promise.all([
          supabase.from('contribution_upvotes').select('contribution_id').in('contribution_id', contribIds),
          supabase.from('contribution_comments').select('contribution_id').in('contribution_id', contribIds),
          supabase.from('corroborations').select('contribution_id').in('contribution_id', contribIds).eq('corroboration_type', 'confirm'),
        ]);
        const upvotes = upvoteResult.data;
        const comments = commentResult.data;
        const corrobs = corrobResult.data;
        for (const u of upvotes || []) upvoteMap[u.contribution_id] = (upvoteMap[u.contribution_id] || 0) + 1;
        for (const c of comments || []) commentMap[c.contribution_id] = (commentMap[c.contribution_id] || 0) + 1;
        for (const c of corrobs || []) corrobMap[c.contribution_id] = (corrobMap[c.contribution_id] || 0) + 1;
      }

      const mapped: DiscoverContribution[] = rows.map((row: any) => ({
        id: row.id,
        title: row.title,
        excerpt: row.excerpt,
        certainty_label: row.certainty_label,
        era_id: row.era_id,
        topic_id: row.topic_id,
        topic_label: row.topic_id ? (topicMap[row.topic_id] || null) : null,
        region: row.region,
        word_count: row.word_count,
        created_at: row.created_at,
        is_anonymous: row.is_anonymous,
        author_id: row.is_anonymous ? null : row.author_id,
        author_name: row.is_anonymous ? null : (nameMap[row.author_id] || null),
        upvote_count: upvoteMap[row.id] || 0,
        comment_count: commentMap[row.id] || 0,
        corroboration_count: corrobMap[row.id] || 0,
      }));

      setContributions(mapped);

      // Build topic options
      const topicCounts: Record<string, { label: string; count: number }> = {};
      for (const row of mapped) {
        if (row.topic_id && row.topic_label) {
          if (!topicCounts[row.topic_id]) topicCounts[row.topic_id] = { label: row.topic_label, count: 0 };
          topicCounts[row.topic_id].count++;
        }
      }
      setTopics(Object.entries(topicCounts).map(([id, v]) => ({ id, label: v.label, count: v.count })).sort((a, b) => b.count - a.count));

      // Build contributor options (non-anonymous)
      const contribCounts: Record<string, { name: string; count: number }> = {};
      for (const row of mapped) {
        if (!row.is_anonymous && row.author_id && row.author_name) {
          if (!contribCounts[row.author_id]) contribCounts[row.author_id] = { name: row.author_name, count: 0 };
          contribCounts[row.author_id].count++;
        }
      }
      setContributors(Object.entries(contribCounts).map(([id, v]) => ({ id, name: v.name, count: v.count })).sort((a, b) => b.count - a.count).slice(0, 30));

      // Identify gaps: era+topic combos with fewer than 3 contributions
      const eraComboCounts: Record<string, number> = {};
      for (const row of mapped) {
        if (row.era_id && row.topic_label) {
          const key = `${row.era_id}||${row.topic_label}`;
          eraComboCounts[key] = (eraComboCounts[key] || 0) + 1;
        }
      }
      const gaps = Object.entries(eraComboCounts)
        .filter(([, count]) => count < 3)
        .map(([key, count]) => {
          const [era, topic] = key.split('||');
          return { era, topic, count };
        })
        .sort((a, b) => a.count - b.count)
        .slice(0, 8);
      setGapEras(gaps);

      setLoading(false);
    }

    load();

    // Real-time subscription: reload when a contribution is published or updated
    const channel = supabase
      .channel('discover-contributions-realtime')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'contributions' },
        (payload) => {
          const newStatus = (payload.new as any)?.status;
          const oldStatus = (payload.old as any)?.status;
          // Only reload when status transitions to/from published
          if (newStatus === 'published' || oldStatus === 'published') {
            load();
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'contributions', filter: 'status=eq.published' },
        () => {
          load();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = keyword.toLowerCase().trim();
    let result = contributions.filter((c) => {
      if (eraFilter && c.era_id !== eraFilter) return false;
      if (topicFilter && c.topic_id !== topicFilter) return false;
      if (certaintyFilter && c.certainty_label !== certaintyFilter) return false;
      if (contributorFilter && c.author_id !== contributorFilter) return false;
      if (q && !c.title.toLowerCase().includes(q) && !(c.excerpt || '').toLowerCase().includes(q) && !(c.author_name || '').toLowerCase().includes(q)) return false;
      return true;
    });

    switch (sortBy) {
      case 'oldest':        result = [...result].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()); break;
      case 'most_upvoted':  result = [...result].sort((a, b) => b.upvote_count - a.upvote_count); break;
      case 'most_discussed':result = [...result].sort((a, b) => b.comment_count - a.comment_count); break;
      case 'corroborated':  result = [...result].sort((a, b) => b.corroboration_count - a.corroboration_count); break;
      default:              result = [...result].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }

    return result;
  }, [contributions, keyword, eraFilter, topicFilter, certaintyFilter, contributorFilter, sortBy]);

  const paginated = useMemo(() => filtered.slice(0, page * PAGE_SIZE), [filtered, page]);
  const hasMore = paginated.length < filtered.length;

  const clearFilters = useCallback(() => {
    setKeyword('');
    setEraFilter('');
    setTopicFilter('');
    setCertaintyFilter('');
    setContributorFilter('');
    setSortBy('newest');
    setPage(1);
  }, []);

  const activeFilterCount = [eraFilter, topicFilter, certaintyFilter, contributorFilter].filter(Boolean).length;

  const totalEras = new Set(contributions.map(c => c.era_id).filter(Boolean)).size;
  const totalTopics = new Set(contributions.map(c => c.topic_id).filter(Boolean)).size;
  const totalContributors = new Set(contributions.filter(c => !c.is_anonymous && c.author_id).map(c => c.author_id)).size;

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
        {/* Header */}
        <div className="border-b" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Compass size={20} style={{ color: 'var(--primary)' }} />
                  <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--primary)' }}>Discover</span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}>
                  Browse All Contributions
                </h1>
                <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
                  Explore published stories by era, topic, contributor, or keyword — and find gaps to fill.
                </p>
              </div>
              <Link
                href="/contribution-submission"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 active:scale-95 self-start sm:self-auto"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                <PenLine size={15} />
                Add a Story
              </Link>
            </div>

            {/* Stats row */}
            {!loading && (
              <div className="flex flex-wrap gap-2 mt-5">
                <StatPill icon={ScrollText} label="Published" value={contributions.length} />
                <StatPill icon={Compass} label="Eras covered" value={totalEras} />
                <StatPill icon={TrendingUp} label="Topics" value={totalTopics} />
                <StatPill icon={Users} label="Contributors" value={totalContributors} />
              </div>
            )}
          </div>
        </div>

        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Search + filter bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
              <input
                type="text"
                placeholder="Search by keyword, title, or contributor…"
                value={keyword}
                onChange={(e) => { setKeyword(e.target.value); setPage(1); }}
                className="w-full pl-9 pr-9 py-2.5 rounded-lg border text-sm outline-none transition-colors"
                style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
              />
              {keyword && (
                <button onClick={() => setKeyword('')} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }}>
                  <X size={14} />
                </button>
              )}
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all duration-150"
              style={{
                backgroundColor: showFilters ? 'var(--primary)' : 'var(--card)',
                borderColor: showFilters ? 'var(--primary)' : 'var(--border)',
                color: showFilters ? 'var(--primary-foreground)' : 'var(--foreground)',
              }}
            >
              <Filter size={14} />
              Filters
              {activeFilterCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-xs font-bold" style={{ backgroundColor: showFilters ? 'rgba(255,255,255,0.25)' : 'var(--primary)', color: showFilters ? 'white' : 'var(--primary-foreground)' }}>
                  {activeFilterCount}
                </span>
              )}
              {showFilters ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>

            <select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
              className="px-3 py-2.5 rounded-lg border text-sm outline-none"
              style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Expanded filters */}
          {showFilters && (
            <div className="rounded-xl border p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
              {/* Era */}
              <div>
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>Era</label>
                <select
                  value={eraFilter}
                  onChange={(e) => { setEraFilter(e.target.value); setPage(1); }}
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                >
                  <option value="">All Eras</option>
                  {ERA_KEYS.map(k => <option key={k} value={k}>{ERA_LABELS[k]}</option>)}
                </select>
              </div>

              {/* Topic */}
              <div>
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>Topic</label>
                <select
                  value={topicFilter}
                  onChange={(e) => { setTopicFilter(e.target.value); setPage(1); }}
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                >
                  <option value="">All Topics</option>
                  {topics.map(t => <option key={t.id} value={t.id}>{t.label} ({t.count})</option>)}
                </select>
              </div>

              {/* Contributor */}
              <div>
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>Contributor</label>
                <select
                  value={contributorFilter}
                  onChange={(e) => { setContributorFilter(e.target.value); setPage(1); }}
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                >
                  <option value="">All Contributors</option>
                  {contributors.map(c => <option key={c.id} value={c.id}>{c.name} ({c.count})</option>)}
                </select>
              </div>

              {/* Certainty */}
              <div>
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>Type</label>
                <select
                  value={certaintyFilter}
                  onChange={(e) => { setCertaintyFilter(e.target.value); setPage(1); }}
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                >
                  <option value="">All Types</option>
                  {Object.entries(CERTAINTY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>

              {activeFilterCount > 0 && (
                <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors"
                    style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
                  >
                    <X size={12} />
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Era quick-filter pills */}
          <div className="flex flex-wrap gap-2 mb-5">
            <button
              onClick={() => { setEraFilter(''); setPage(1); }}
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150"
              style={{
                backgroundColor: !eraFilter ? 'var(--primary)' : 'var(--card)',
                borderColor: !eraFilter ? 'var(--primary)' : 'var(--border)',
                color: !eraFilter ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
              }}
            >
              All Eras
            </button>
            {ERA_KEYS.map(k => (
              <button
                key={k}
                onClick={() => { setEraFilter(eraFilter === k ? '' : k); setPage(1); }}
                className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150"
                style={{
                  backgroundColor: eraFilter === k ? 'var(--primary)' : 'var(--card)',
                  borderColor: eraFilter === k ? 'var(--primary)' : 'var(--border)',
                  color: eraFilter === k ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
                }}
              >
                {ERA_LABELS[k]}
              </button>
            ))}
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Main grid */}
            <div className="flex-1 min-w-0">
              {loading ? (
                <div className="flex items-center justify-center py-24">
                  <Loader2 size={28} className="animate-spin" style={{ color: 'var(--primary)' }} />
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <ScrollText size={36} className="mb-3" style={{ color: 'var(--muted-foreground)', opacity: 0.4 }} />
                  <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>No contributions match your filters</p>
                  <p className="text-xs mt-1 mb-4" style={{ color: 'var(--muted-foreground)' }}>Try adjusting your search or clearing filters</p>
                  <button onClick={clearFilters} className="text-xs px-4 py-2 rounded-lg" style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                    Clear filters
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      Showing <span className="font-semibold" style={{ color: 'var(--foreground)' }}>{paginated.length}</span> of <span className="font-semibold" style={{ color: 'var(--foreground)' }}>{filtered.length}</span> contributions
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {paginated.map(item => <ContributionCard key={item.id} item={item} />)}
                  </div>
                  {hasMore && (
                    <div className="flex justify-center mt-8">
                      <button
                        onClick={() => setPage(p => p + 1)}
                        className="px-6 py-2.5 rounded-lg border text-sm font-medium transition-all duration-150"
                        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--primary)';
                          (e.currentTarget as HTMLButtonElement).style.color = 'var(--primary)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)';
                          (e.currentTarget as HTMLButtonElement).style.color = 'var(--foreground)';
                        }}
                      >
                        Load more ({filtered.length - paginated.length} remaining)
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Sidebar: Gaps to fill */}
            {!loading && gapEras.length > 0 && (
              <aside className="lg:w-72 shrink-0">
                <div className="sticky top-20 rounded-xl border p-4" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={14} style={{ color: 'var(--primary)' }} />
                    <h2 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Gaps to Fill</h2>
                  </div>
                  <p className="text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>
                    These era–topic combinations have fewer than 3 stories. Be the first to contribute.
                  </p>
                  <div className="flex flex-col gap-2">
                    {gapEras.map((g, i) => (
                      <GapBadge key={i} era={g.era} topic={g.topic} count={g.count} />
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                    <Link
                      href="/contribution-submission"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150"
                      style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                    >
                      <PenLine size={14} />
                      Start Contributing
                    </Link>
                  </div>
                </div>
              </aside>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
